import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { apiClient } from "@/App";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  Gift,
  Star,
  Trophy,
  Sparkles,
  ArrowUpRight,
  ShoppingBag,
  Calendar,
  Dumbbell,
} from "lucide-react";

const BeWallet = () => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeemingReward, setRedeemingReward] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletRes, txnsRes] = await Promise.all([
        apiClient.get("/wallet"),
        apiClient.get("/wallet/transactions"),
      ]);
      setWallet(walletRes.data);
      setTransactions(txnsRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load wallet data");
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!redeemingReward) return;
    setRedeeming(true);
    try {
      const res = await apiClient.post("/wallet/redeem", {
        points: redeemingReward.points,
        reward_name: redeemingReward.name,
      });
      toast.success(res.data.message || `Redeemed ${redeemingReward.name}!`);
      setRedeemingReward(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Redemption failed");
    } finally {
      setRedeeming(false);
    }
  };

  const tierInfo = {
    bronze: { next: "Silver", points: 1000, color: "bg-orange-600", benefits: ["5% on purchases", "Basic rewards"] },
    silver: { next: "Gold", points: 5000, color: "bg-gray-400", benefits: ["7% on purchases", "Early access", "Birthday bonus"] },
    gold: { next: "Platinum", points: 10000, color: "bg-yellow-500", benefits: ["10% on purchases", "VIP events", "Free shipping"] },
    platinum: { next: null, points: 10000, color: "bg-purple-500", benefits: ["15% on purchases", "Exclusive drops", "Personal stylist"] },
  };

  const rewards = [
    { name: "KES 1,000 Store Credit", points: 1000, icon: ShoppingBag },
    { name: "Event Priority Access", points: 500, icon: Calendar },
    { name: "Free Training Session", points: 2000, icon: Dumbbell },
    { name: "BE Merch Pack", points: 3000, icon: Gift },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const currentTier = wallet?.tier || "bronze";
  const tier = tierInfo[currentTier];
  const progress = tier.next
    ? Math.min((wallet?.points_balance || 0) / tier.points * 100, 100)
    : 100;

  const getTransactionIcon = (type) => {
    switch (type) {
      case "earn":
        return <TrendingUp className="w-5 h-5 text-secondary" />;
      case "redeem":
        return <TrendingDown className="w-5 h-5 text-destructive" />;
      case "bonus":
        return <Sparkles className="w-5 h-5 text-accent" />;
      default:
        return <Star className="w-5 h-5 text-primary" />;
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="be-wallet-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
              <Wallet className="w-6 h-6 text-accent-foreground" />
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl">BE Wallet</h1>
          </div>
          <p className="text-muted-foreground">
            Your rewards, points, and exclusive benefits.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="wallet-card text-foreground overflow-hidden" data-testid="wallet-card">
                <CardContent className="p-8 relative z-10">
                  <div className="flex items-start justify-between mb-8">
                    <div>
                      <p className="text-sm opacity-80 mb-1">Total Balance</p>
                      <div className="font-heading font-bold text-5xl points-counter" data-testid="points-balance">
                        {wallet?.points_balance?.toLocaleString() || 0}
                      </div>
                      <p className="text-sm opacity-80 mt-1">BE Points</p>
                    </div>
                    <Badge className={`${tier.color} text-white uppercase font-bold px-3 py-1`}>
                      {currentTier}
                    </Badge>
                  </div>

                  {tier.next && (
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="opacity-80">Progress to {tier.next}</span>
                        <span className="font-medium">
                          {tier.points - (wallet?.points_balance || 0)} pts to go
                        </span>
                      </div>
                      <Progress value={progress} className="h-2 bg-black/20" />
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Trophy className={`w-5 h-5 ${currentTier === "bronze" ? "text-orange-600" : currentTier === "silver" ? "text-gray-400" : currentTier === "gold" ? "text-yellow-500" : "text-purple-500"}`} />
                    {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)} Benefits
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {tier.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                        <Star className="w-5 h-5 text-accent" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  {transactions.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground" data-testid="no-transactions">
                      No transactions yet
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {transactions.map((txn) => (
                        <div
                          key={txn.transaction_id}
                          className="flex items-center justify-between p-4 bg-muted/30 rounded-xl"
                          data-testid={`txn-${txn.transaction_id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-card rounded-xl flex items-center justify-center">
                              {getTransactionIcon(txn.type)}
                            </div>
                            <div>
                              <p className="font-medium">{txn.description}</p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(txn.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`font-heading font-bold text-lg ${
                              txn.amount > 0 ? "text-secondary" : "text-destructive"
                            }`}
                          >
                            {txn.amount > 0 ? "+" : ""}{txn.amount}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Gift className="w-5 h-5 text-primary" />
                    Redeem Rewards
                  </CardTitle>
                  <CardDescription>Use your points for exclusive perks</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {rewards.map((reward, index) => (
                    <div
                      key={index}
                      className="p-4 border border-border rounded-xl hover:border-primary transition-colors"
                      data-testid={`reward-card-${index}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <reward.icon className="w-5 h-5 text-primary" />
                          </div>
                          <span className="font-medium">{reward.name}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          {reward.points.toLocaleString()} points
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={(wallet?.points_balance || 0) < reward.points}
                          onClick={() => setRedeemingReward(reward)}
                          data-testid={`redeem-${index}`}
                        >
                          Redeem
                          <ArrowUpRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-accent" />
                    Ways to Earn
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { action: "Make a purchase", points: "5% back" },
                      { action: "Complete body scan", points: "+100" },
                      { action: "Subscribe to BE Fit Box", points: "+300-1000" },
                      { action: "RSVP to events", points: "+200" },
                      { action: "Post in community", points: "+25" },
                      { action: "Refer a friend", points: "+300" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-2">
                        <span className="text-sm">{item.action}</span>
                        <Badge variant="outline" className="bg-accent/10 text-accent-foreground border-accent/20">
                          {item.points}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>

      <AlertDialog open={!!redeemingReward} onOpenChange={(open) => !open && setRedeemingReward(null)}>
        <AlertDialogContent data-testid="redeem-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Redeem {redeemingReward?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to spend{" "}
              <span className="font-bold text-primary">
                {redeemingReward?.points?.toLocaleString()} BE Points
              </span>{" "}
              for <span className="font-medium">{redeemingReward?.name}</span>.
              Your remaining balance will be{" "}
              <span className="font-bold">
                {((wallet?.points_balance || 0) - (redeemingReward?.points || 0)).toLocaleString()}{" "}
                points
              </span>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="redeem-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRedeem}
              disabled={redeeming}
              data-testid="redeem-confirm-btn"
            >
              {redeeming ? "Processing..." : "Confirm Redeem"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default BeWallet;
